import { ChangeDetectionStrategy, Component, inject, signal, effect, input, untracked } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { SiteService } from '../../../core/services/site.service';
import { SiteAlertSettings, NoiseAlertSettings, PhAlertSettings, TiltAlertSettings } from '../../../shared/interfaces';

@Component({
  selector: 'app-site-alert-manage',
  templateUrl: './site-alert-manage.html',
  styleUrl: './site-alert-manage.scss',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    RouterModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteAlertManageComponent {
  private readonly fb = inject(FormBuilder);
  private readonly siteService = inject(SiteService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);

  public readonly id = input.required<string>();
  public readonly isLoading = signal<boolean>(true);
  public readonly emails = signal<string[]>([]);
  public readonly emailError = signal<string | null>(null);
  public readonly separatorKeyCodes = [ENTER, COMMA] as const;
  private isInitialized = false;

  public readonly form = this.fb.group({
    noise: this.fb.group({
      noiseThreshold: [null as number | null],
      vibrationThreshold: [null as number | null],
    }),
    ph: this.fb.group({
      minThreshold: [null as number | null],
      maxThreshold: [null as number | null],
    }),
    tilt: this.fb.group({
      absoluteDisplacementThreshold: [null as number | null],
      temporalDisplacementThreshold: [null as number | null],
    })
  });

  constructor() {
    effect(() => {
      const globalLoading = this.siteService.isLoading();
      
      if (!globalLoading && !this.isInitialized) {
        untracked(() => {
          this.isInitialized = true;
          const siteId = this.id();
          const detail = this.siteService.getSiteDetail(siteId);
          if (detail?.alertSettings) {
            this.form.patchValue(detail.alertSettings, { emitEvent: false });
          }
          this.emails.set(detail?.emails ?? []);
          // Whether found or not, loading is done
          this.isLoading.set(false);
        });
      }
    });
  }

  public async save(): Promise<void> {
    const siteId = this.id();
    if (!siteId || this.form.invalid) return;

    try {
      this.isLoading.set(true);
      const formValue = this.form.value;
      
      // クリーンアップ: null または未定義のプロパティを除外し、空のグループを取り除く
      const settings: Partial<SiteAlertSettings> = {};
      
      if (formValue.noise) {
        const noiseSettings: Partial<NoiseAlertSettings> = {};
        if (formValue.noise.noiseThreshold !== null) noiseSettings.noiseThreshold = formValue.noise.noiseThreshold;
        if (formValue.noise.vibrationThreshold !== null) noiseSettings.vibrationThreshold = formValue.noise.vibrationThreshold;
        if (Object.keys(noiseSettings).length > 0) settings.noise = noiseSettings;
      }

      if (formValue.ph) {
        const phSettings: Partial<PhAlertSettings> = {};
        if (formValue.ph.minThreshold !== null) phSettings.minThreshold = formValue.ph.minThreshold;
        if (formValue.ph.maxThreshold !== null) phSettings.maxThreshold = formValue.ph.maxThreshold;
        if (Object.keys(phSettings).length > 0) settings.ph = phSettings;
      }

      if (formValue.tilt) {
        const tiltSettings: Partial<TiltAlertSettings> = {};
        if (formValue.tilt.absoluteDisplacementThreshold !== null) tiltSettings.absoluteDisplacementThreshold = formValue.tilt.absoluteDisplacementThreshold;
        if (formValue.tilt.temporalDisplacementThreshold !== null) tiltSettings.temporalDisplacementThreshold = formValue.tilt.temporalDisplacementThreshold;
        if (Object.keys(tiltSettings).length > 0) settings.tilt = tiltSettings;
      }

      await this.siteService.updateAlertSettings(siteId, settings, this.emails());
      await this.siteService.reloadSites();
      
      this.snackBar.open('アラート設定を保存しました', '閉じる', { duration: 3000 });
      this.router.navigate(['/sites/manage']);
    } catch (error) {
      console.error('アラート設定の保存に失敗しました:', error);
      this.snackBar.open('保存に失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.isLoading.set(false);
    }
  }

  public addEmail(event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim();
    if (!value) {
      this.emailError.set(null);
      event.chipInput?.clear();
      return;
    }

    const email = value.toLowerCase();

    if (!this.isValidEmail(email)) {
      this.emailError.set('メールアドレスの形式が正しくありません');
      return;
    }

    if (this.emails().includes(email)) {
      this.emailError.set('同じメールアドレスは追加できません');
      return;
    }

    this.emails.update(emails => [...emails, email]);
    this.emailError.set(null);
    event.chipInput?.clear();
  }

  public removeEmail(email: string): void {
    this.emails.update(emails => emails.filter(item => item !== email));
    this.emailError.set(null);
  }

  private isValidEmail(email: string): boolean {
    return /^[a-zA-Z0-9._%+-]+@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(email);
  }
}
